<script setup lang="ts">
import {computed, ref} from "vue";
import {useElementSize} from "@vueuse/core";
import {storeToRefs} from "pinia";
import {ArrowRight, CalendarClock} from "@lucide/vue";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {useCreditStore} from "@/stores/credit.store";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import useUpcomingSequenceMessageSummary from "@/notification-sequences/composables/useUpcomingSequenceMessageSummary";
import {UpcomingSequenceMessageWeek} from "@/notification-sequences/interfaces/NotificationSequence";
import {pluralize} from "@/core/filters/Pluralize";

const {days = 92} = defineProps<{
    /** The size of the plotted window, in days. */
    days?: number;
}>();

const {credit} = storeToRefs(useCreditStore());
const {data: summary} = useUpcomingSequenceMessageSummary(days);

const rail = ref<HTMLElement | null>(null);
const {width: railWidth} = useElementSize(rail);

interface TimelineDot {
    weekStart: string;
    label: string;
    count: number;
    credits: number;
    /** Percentage offset along the rail. */
    left: number;
    size: number;
    over: boolean;
    /** The sequences listed in the tooltip, capped to keep it readable. */
    sequences: {name: string; count: number}[];
    /** Messages left out of the listed sequences, 0 when everything fits. */
    hiddenCount: number;
}

const balance = computed(() => credit.value?.balance ?? 0);
const total = computed(() => summary.value?.total ?? 0);
const totalCredits = computed(() => summary.value?.total_credits ?? 0);

const MIN_DOT_SIZE = 8;
const MAX_DOT_SIZE = 34;

/** Share of the space between two weeks a dot may take, leaving a visible gap. */
const DOT_PITCH_RATIO = 0.8;

/** How many sequence lines a tooltip lists before collapsing the rest. */
const MAX_TOOLTIP_SEQUENCES = 10;

/**
 * The largest diameter that still keeps neighbouring weeks apart.
 *
 * The rail shrinks with the window, so a fixed maximum makes the busiest dots
 * collide once the pitch drops below their size.
 */
const maxDotSize = computed(() => {
    const weekCount = summary.value?.weeks?.length ?? 0;

    if (!railWidth.value || weekCount < 2) {
        return MAX_DOT_SIZE;
    }

    const pitch = (railWidth.value * 0.96) / (weekCount - 1);

    return Math.max(Math.min(MAX_DOT_SIZE, pitch * DOT_PITCH_RATIO), MIN_DOT_SIZE);
});

/**
 * Plot each send week on the rail, flagging the ones the credit balance no longer covers.
 *
 * The dots are sized by message volume, but the overflow runs on the credit cost:
 * an SMS is billed per segment, so a week can exhaust the balance with fewer
 * messages than it appears to hold. A week is positioned on its midpoint, so its
 * dot sits between the two month marks it spans.
 */
const dots = computed<TimelineDot[]>(() => {
    const weeks = summary.value?.weeks ?? [];
    const from = dayjs(summary.value?.from);
    const span = Math.max(dayjs(summary.value?.to).diff(from, 'day'), 1);
    const busiest = Math.max(...weeks.map(week => week.count), 1);

    let running = 0;

    return weeks.map((week: UpcomingSequenceMessageWeek) => {
        running += week.credits;

        const midpoint = dayjs(week.week_start).add(3, 'day').diff(from, 'day');

        const listed = week.sequences.slice(0, MAX_TOOLTIP_SEQUENCES);

        return {
            weekStart: week.week_start,
            label: `Semaine du ${dayjs(week.week_start).locale('fr').format('D MMMM')}`,
            count: week.count,
            credits: week.credits,
            left: Math.min(Math.max((midpoint / span) * 96 + 2, 2), 98),
            size: dotSize(week.count, busiest, maxDotSize.value),
            over: balance.value < running,
            sequences: listed,
            hiddenCount: week.sequences
                .slice(MAX_TOOLTIP_SEQUENCES)
                .reduce((sum, sequence) => sum + sequence.count, 0),
        };
    });
});

/**
 * Size a dot relative to the busiest week, so the spread uses the whole range
 * whatever the absolute volumes are.
 */
const dotSize = (count: number, busiest: number, max: number): number => {
    const ratio = busiest > 1 ? (count - 1) / (busiest - 1) : 1;
    const area = MIN_DOT_SIZE ** 2 + (max ** 2 - MIN_DOT_SIZE ** 2) * ratio;

    return Math.round(Math.sqrt(area));
};

/**
 * The horizon actually covered: the end of the last populated week, rather than
 * the window edge which usually runs past the last scheduled send.
 */
const lastSendLabel = computed(() => {
    const lastWeek = summary.value?.weeks?.at(-1);

    if (!lastWeek) {
        return null;
    }

    const windowEnd = dayjs(summary.value?.to);
    const weekEnd = dayjs(lastWeek.week_end);

    return (weekEnd.isBefore(windowEnd) ? weekEnd : windowEnd)
        .locale('fr')
        .format('D MMMM YYYY');
});

/** The rail offset where the balance runs out, to place the marker. */
const overflowLeft = computed(() => dots.value.find(dot => dot.over)?.left ?? null);

/** Room a month label needs before the next one may be drawn. */
const MIN_MONTH_MARK_GAP = 34;

/**
 * The month labels under the rail.
 *
 * Only months starting inside the window get a mark: the window opens mid-month, so
 * anchoring the first one at its 1st would clamp it onto the next label. Labels are
 * then dropped while they would not fit, keeping the first of each competing pair.
 */
const monthMarks = computed(() => {
    const from = dayjs(summary.value?.from);
    const to = dayjs(summary.value?.to);
    const span = Math.max(to.diff(from, 'day'), 1);
    const marks: {label: string; left: number}[] = [];

    for (let month = from.startOf('month'); month.isBefore(to); month = month.add(1, 'month')) {
        if (month.isBefore(from, 'day')) {
            continue;
        }

        const left = (month.diff(from, 'day') / span) * 96 + 2;
        const previous = marks.at(-1);

        if (previous && railWidth.value && (left - previous.left) / 100 * railWidth.value < MIN_MONTH_MARK_GAP) {
            continue;
        }

        marks.push({label: month.locale('fr').format('MMM'), left});
    }

    return marks;
});
</script>

<template>
  <div v-if="total > 0" class="bg-lightPurple-lightest rounded-xl px-6 py-4 flex flex-col gap-4">
    <div class="flex items-center gap-6 flex-wrap">
      <div class="flex items-center gap-4 shrink-0">
        <div class="size-11 rounded-xl bg-white flex items-center justify-center shrink-0">
          <CalendarClock class="text-primary size-6" />
        </div>
        <div class="flex flex-col">
          <span class="text-primary font-medium whitespace-nowrap">
            {{ total }} RCS {{ pluralize('programmé', total) }}
          </span>
          <span v-if="lastSendLabel" class="text-xs text-muted-foreground whitespace-nowrap">
            jusqu'au {{ lastSendLabel }} · {{ totalCredits }} {{ pluralize('crédit', totalCredits) }}
          </span>
        </div>
      </div>

      <div ref="rail" class="flex-1 min-w-[320px] relative h-[76px]">
        <div class="absolute left-0 right-0 top-[38px] h-0.5 bg-primary/15 rounded-sm" />

        <template v-if="overflowLeft !== null">
          <div
            class="absolute top-1.5 bottom-5 w-0.5 overflow-marker"
            :style="{left: `${overflowLeft}%`}"
          />
          <div
            class="absolute top-0 -translate-x-1/2 text-[9.5px] font-bold text-red-500 whitespace-nowrap"
            :style="{left: `${overflowLeft}%`}"
          >
            solde épuisé
          </div>
        </template>

        <TooltipProvider :delay-duration="100">
          <Tooltip v-for="dot in dots" :key="dot.weekStart">
            <TooltipTrigger as-child>
              <div
                class="absolute top-[38px] rounded-full border-2 border-white shadow cursor-pointer hover:scale-105 transition-transform duration-200"
                :class="dot.over ? 'bg-red-500' : 'bg-primary'"
                :style="{
                  left: `${dot.left}%`,
                  width: `${dot.size}px`,
                  height: `${dot.size}px`,
                  marginLeft: `${-dot.size / 2}px`,
                  marginTop: `${-dot.size / 2}px`,
                }"
              />
            </TooltipTrigger>
            <TooltipContent
              :collision-padding="8"
              class="bg-gray-dark text-white px-3.5 py-2.5 text-xs leading-relaxed max-w-none"
            >
              <div class="font-bold">
                {{ dot.label }} · {{ dot.count }} {{ pluralize('envoi', dot.count) }}
              </div>
              <div
                v-for="sequence in dot.sequences"
                :key="sequence.name"
                class="flex items-center gap-2 text-gray"
              >
                <span class="size-1.5 rounded-full bg-secondary shrink-0" />
                {{ sequence.name }} <span class="text-gray/70">× {{ sequence.count }}</span>
              </div>
              <div v-if="dot.hiddenCount > 0" class="text-gray/70 pl-3.5">
                et {{ dot.hiddenCount }} {{ pluralize('autre', dot.hiddenCount) }}
              </div>
              <div v-if="dot.over" class="text-red-300 font-medium">
                ⚠ au-delà du solde de crédits
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div
          v-for="mark in monthMarks"
          :key="mark.label"
          class="absolute bottom-0 text-[11px] font-medium text-muted-foreground capitalize"
          :style="{left: `${mark.left}%`}"
        >
          {{ mark.label }}
        </div>
      </div>
    </div>

    <div class="flex items-center gap-4 flex-wrap border-t border-primary/10 pt-3.5">
      <router-link
        :to="{name: 'rcs.scheduled'}"
        class="text-primary font-medium text-sm whitespace-nowrap ml-auto hover:underline inline-flex items-center gap-1"
      >
        Voir les RCS programmés
        <ArrowRight class="size-4 shrink-0" />
      </router-link>
    </div>
  </div>
</template>

<style scoped>
.overflow-marker {
    background: repeating-linear-gradient(to bottom, theme('colors.red.500') 0 4px, transparent 4px 8px);
}
</style>
